import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import type { FinDashboard, FinDashDimRow, FinDashItem, FinDashRankRow } from "../api/finTypes";
import { FIN_ENTRY_PAGE, FIN_PAYABLES_PAGE, FIN_RECEIVABLES_PAGE } from "../routes";
import { formatCurrency, formatDateOnly } from "../../dp/utils/format";
import { periodLabel } from "../utils/fin";

/**
 * Painel de contas a pagar e a receber (17/10/2026). Cores validadas para daltonismo
 * (verde e âmbar, ΔE protan 13); o valor sempre vem escrito ao lado da barra.
 */
const COLORS = {
  in: "#1F7A45",
  inSoft: "#A7D3B6",
  out: "#E08A1E",
  outSoft: "#F5CF9C",
};

/** Filtros que um clique no painel pode aplicar (drill-down). */
export type DashDrill = "centro" | "loja" | "categoria";

type Props = {
  data: FinDashboard;
  onDrill: (key: DashDrill, id: string) => void;
};

const num = (v: string) => Number(v) || 0;

const compact = (value: number) =>
  Math.abs(value) >= 1_000_000
    ? `${(value / 1_000_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mi`
    : Math.abs(value) >= 1_000
      ? `${(value / 1_000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} mil`
      : value.toLocaleString("pt-BR");

/** Barra horizontal fina, proporcional ao maior valor da lista. */
const Meter = ({ value, max, color }: { value: number; max: number; color: string }) => (
  <span className="finDash__track" aria-hidden="true">
    <span
      className="finDash__fill"
      style={{ width: `${max > 0 ? Math.max(value > 0 ? 2 : 0, (value / max) * 100) : 0}%`, background: color }}
    />
  </span>
);

const Swatch = ({ color }: { color: string }) => (
  <span className="finDash__swatch" style={{ background: color }} aria-hidden="true" />
);

/** Lista com duas barras por linha: a pagar (âmbar) e a receber (verde). */
function DimList({
  rows,
  onPick,
  emptyText,
}: {
  rows: Array<{ id: string | null; name: string; out: number; in: number; outOpen: number; inOpen: number }>;
  onPick?: (id: string) => void;
  emptyText: string;
}) {
  const visible = rows.filter((r) => r.out > 0 || r.in > 0);
  if (!visible.length) return <p className="finDash__empty">{emptyText}</p>;
  const max = Math.max(...visible.flatMap((r) => [r.out, r.in]));
  return (
    <ul className="finDash__list">
      {visible.map((r) => {
        const clickable = Boolean(onPick && r.id);
        const body = (
          <>
            <span className="finDash__name">{r.name}</span>
            <span className="finDash__pair">
              <Meter value={r.out} max={max} color={COLORS.out} />
              <span className="finDash__val">
                {formatCurrency(r.out)}
                {r.outOpen > 0 ? <small> · aberto {formatCurrency(r.outOpen)}</small> : null}
              </span>
            </span>
            <span className="finDash__pair">
              <Meter value={r.in} max={max} color={COLORS.in} />
              <span className="finDash__val">
                {formatCurrency(r.in)}
                {r.inOpen > 0 ? <small> · aberto {formatCurrency(r.inOpen)}</small> : null}
              </span>
            </span>
          </>
        );
        return (
          <li key={r.id ?? `sem-${r.name}`}>
            {clickable ? (
              <button type="button" className="finDash__row finDash__row--btn" onClick={() => onPick!(r.id!)} title="Filtrar por este item">
                {body}
              </button>
            ) : (
              <div className="finDash__row">{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

const dimRows = (rows: FinDashDimRow[]) =>
  rows.map((r) => ({
    id: r.id,
    name: r.name,
    out: num(r.payable.amount),
    in: num(r.receivable.amount),
    outOpen: num(r.payable.open),
    inOpen: num(r.receivable.open),
  }));

/** Ranking de uma série só (categorias, fornecedores), com o em aberto ao lado. */
function RankList({
  rows,
  color,
  onPick,
  emptyText,
}: {
  rows: FinDashRankRow[];
  color: string;
  onPick?: (id: string) => void;
  emptyText: string;
}) {
  if (!rows.length) return <p className="finDash__empty">{emptyText}</p>;
  const max = Math.max(...rows.map((r) => num(r.amount)));
  return (
    <ul className="finDash__list">
      {rows.map((r, i) => {
        const clickable = Boolean(onPick && r.id && !r.others);
        const body = (
          <>
            <span className="finDash__name">
              {r.name}
              {r.parentName ? <small className="finDash__parent"> · {r.parentName}</small> : null}
              <small className="finDash__count"> · {r.count} título{r.count === 1 ? "" : "s"}</small>
            </span>
            <span className="finDash__pair">
              <Meter value={num(r.amount)} max={max} color={color} />
              <span className="finDash__val">
                {formatCurrency(r.amount)}
                {num(r.open) > 0 ? <small> · aberto {formatCurrency(r.open)}</small> : null}
              </span>
            </span>
          </>
        );
        return (
          <li key={r.id ?? `outros-${i}`}>
            {clickable ? (
              <button type="button" className="finDash__row finDash__row--btn" onClick={() => onPick!(r.id!)} title="Filtrar por este item">
                {body}
              </button>
            ) : (
              <div className="finDash__row">{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ItemList({ items, today, emptyText }: { items: FinDashItem[]; today: string; emptyText: string }) {
  const navigate = useNavigate();
  if (!items.length) return <p className="finDash__empty">{emptyText}</p>;
  return (
    <ul className="finDash__items">
      {items.map((it) => (
        <li key={it.id}>
          <button type="button" className="finDash__item" onClick={() => navigate(FIN_ENTRY_PAGE(it.id))}>
            <span className={`dpBadge ${it.type === "PAGAR" ? "dpBadge--orange" : "dpBadge--green"}`}>
              {it.type === "PAGAR" ? "Pagar" : "Receber"}
            </span>
            <span className="finDash__itemText">
              <strong>{it.description}</strong>
              <small>
                {it.counterparty ? `${it.counterparty} · ` : ""}
                {it.dueDate === today ? "vence hoje" : `vence ${formatDateOnly(it.dueDate)}`}
              </small>
            </span>
            <span className="finDash__itemValue">{formatCurrency(it.open)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

type Side = "PAGAR" | "RECEBER";

export default function CashDashboard({ data, onDrill }: Props) {
  const navigate = useNavigate();
  const [categorySide, setCategorySide] = useState<Side>("PAGAR");

  const { payable, receivable } = data.totals;
  const forecastResult = num(receivable.amount) - num(payable.amount);
  const realizedResult = num(data.realized.in) - num(data.realized.out);

  const series = data.series.map((b) => ({
    period: periodLabel(b.period),
    receivedPart: num(b.receivable) - num(b.receivableOpen),
    receivableOpen: num(b.receivableOpen),
    paidPart: num(b.payable) - num(b.payableOpen),
    payableOpen: num(b.payableOpen),
  }));
  const hasSeries = series.some((s) => s.receivedPart || s.receivableOpen || s.paidPart || s.payableOpen);
  const agingMax = Math.max(0, ...data.aging.flatMap((a) => [num(a.payable.amount), num(a.receivable.amount)]));
  const baseLabel = data.basis === "competence" ? "competência" : "vencimento";

  return (
    <div className="finDash">
      <div className="finSummary">
        <button type="button" className="finStat" onClick={() => navigate(FIN_PAYABLES_PAGE())}>
          <span className="finStat__label">
            <Swatch color={COLORS.out} /> A pagar no período
          </span>
          <span className="finStat__value">{formatCurrency(payable.amount)}</span>
          <span className="finStat__hint">
            {payable.count} título{payable.count === 1 ? "" : "s"} · pago {formatCurrency(payable.settled)} · aberto{" "}
            {formatCurrency(payable.open)}
          </span>
        </button>
        <button type="button" className="finStat" onClick={() => navigate(FIN_RECEIVABLES_PAGE())}>
          <span className="finStat__label">
            <Swatch color={COLORS.in} /> A receber no período
          </span>
          <span className="finStat__value">{formatCurrency(receivable.amount)}</span>
          <span className="finStat__hint">
            {receivable.count} título{receivable.count === 1 ? "" : "s"} · recebido {formatCurrency(receivable.settled)} ·
            aberto {formatCurrency(receivable.open)}
          </span>
        </button>
        <div className={`finStat ${forecastResult < 0 ? "finStat--bad" : ""}`}>
          <span className="finStat__label">Resultado do período</span>
          <span className="finStat__value">{formatCurrency(forecastResult)}</span>
          <span className="finStat__hint">a receber − a pagar, pelo {baseLabel}</span>
        </div>
        <div className={`finStat ${realizedResult < 0 ? "finStat--bad" : ""}`}>
          <span className="finStat__label">Realizado no período</span>
          <span className="finStat__value">{formatCurrency(realizedResult)}</span>
          <span className="finStat__hint">
            recebido {formatCurrency(data.realized.in)} · pago {formatCurrency(data.realized.out)}
          </span>
        </div>
        <button
          type="button"
          className={`finStat ${data.overdue.payable.count + data.overdue.receivable.count > 0 ? "finStat--warn" : ""}`}
          onClick={() => navigate(`${FIN_PAYABLES_PAGE()}?situacao=atrasados&periodo=todos`)}
        >
          <span className="finStat__label">Atrasados (todos)</span>
          <span className="finStat__value">{formatCurrency(data.overdue.payable.amount)}</span>
          <span className="finStat__hint">
            {data.overdue.payable.count} a pagar · a receber {formatCurrency(data.overdue.receivable.amount)} (
            {data.overdue.receivable.count})
          </span>
        </button>
      </div>

      <section className="ecoCard finDash__card">
        <header className="finDash__head">
          <h2>A pagar e a receber por {data.granularity === "day" ? "dia" : "mês"}</h2>
          <p>Valor dos títulos pelo {baseLabel}; a parte clara é o que ainda está em aberto.</p>
        </header>
        {hasSeries ? (
          <div className="finDash__chart">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2} barCategoryGap="22%">
                <CartesianGrid vertical={false} stroke="var(--eco-border-soft, #e5ebe6)" />
                <XAxis dataKey="period" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "var(--eco-muted)" }} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={64}
                  tick={{ fontSize: 12, fill: "var(--eco-muted)" }}
                  tickFormatter={(v: number) => compact(v)}
                />
                <Tooltip
                  cursor={{ fill: "rgba(14,31,20,0.05)" }}
                  formatter={(value: number, name: string) => [formatCurrency(value), name]}
                  contentStyle={{ borderRadius: 10, borderColor: "var(--eco-border)", fontSize: 13 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" />
                <Bar dataKey="receivedPart" name="Recebido" stackId="in" fill={COLORS.in} />
                <Bar dataKey="receivableOpen" name="A receber (aberto)" stackId="in" fill={COLORS.inSoft} radius={[4, 4, 0, 0]} />
                <Bar dataKey="paidPart" name="Pago" stackId="out" fill={COLORS.out} />
                <Bar dataKey="payableOpen" name="A pagar (aberto)" stackId="out" fill={COLORS.outSoft} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="finDash__empty">Nenhum título no período com esses filtros.</p>
        )}
      </section>

      <div className="finDash__grid">
        <section className="ecoCard finDash__card">
          <header className="finDash__head">
            <h2>Em aberto por vencimento</h2>
            <p>Tudo o que falta pagar e receber, de qualquer período.</p>
          </header>
          <ul className="finDash__list">
            {data.aging.map((a) => (
              <li key={a.key}>
                <div className={`finDash__row ${a.key.startsWith("late") || a.key === "over60" ? "finDash__row--late" : ""}`}>
                  <span className="finDash__name">{a.label}</span>
                  <span className="finDash__pair">
                    <Meter value={num(a.payable.amount)} max={agingMax} color={COLORS.out} />
                    <span className="finDash__val">
                      {formatCurrency(a.payable.amount)}
                      {a.payable.count ? <small> · {a.payable.count}</small> : null}
                    </span>
                  </span>
                  <span className="finDash__pair">
                    <Meter value={num(a.receivable.amount)} max={agingMax} color={COLORS.in} />
                    <span className="finDash__val">
                      {formatCurrency(a.receivable.amount)}
                      {a.receivable.count ? <small> · {a.receivable.count}</small> : null}
                    </span>
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <DashLegend />
        </section>

        <section className="ecoCard finDash__card">
          <header className="finDash__head">
            <h2>Por centro de custo</h2>
            <p>Clique para filtrar o painel por ele.</p>
          </header>
          <DimList
            rows={dimRows(data.byCostCenter)}
            onPick={(id) => onDrill("centro", id)}
            emptyText="Nenhum título no período."
          />
          <DashLegend />
        </section>

        <section className="ecoCard finDash__card">
          <header className="finDash__head">
            <h2>Por loja</h2>
            <p>Loja do título; sem ela, a do centro de custo quando ele tem uma loja só.</p>
          </header>
          <DimList rows={dimRows(data.byStore)} onPick={(id) => onDrill("loja", id)} emptyText="Nenhum título no período." />
          <DashLegend />
        </section>

        <section className="ecoCard finDash__card">
          <header className="finDash__head finDash__head--row">
            <div>
              <h2>Por categoria</h2>
              <p>As maiores; o resto em "Outros".</p>
            </div>
            <EcoSegmented<Side>
              label="Tipo"
              options={[
                { value: "PAGAR", label: "Pagar" },
                { value: "RECEBER", label: "Receber" },
              ]}
              value={categorySide}
              onChange={setCategorySide}
            />
          </header>
          <RankList
            rows={categorySide === "PAGAR" ? data.byCategory.payable : data.byCategory.receivable}
            color={categorySide === "PAGAR" ? COLORS.out : COLORS.in}
            onPick={(id) => onDrill("categoria", id)}
            emptyText="Nenhum título no período."
          />
        </section>

        <section className="ecoCard finDash__card">
          <header className="finDash__head">
            <h2>Principais fornecedores</h2>
            <p>Contas a pagar do período.</p>
          </header>
          <RankList rows={data.byCounterparty.payable} color={COLORS.out} emptyText="Nenhuma conta a pagar no período." />
        </section>

        <section className="ecoCard finDash__card">
          <header className="finDash__head">
            <h2>Principais clientes</h2>
            <p>Contas a receber do período.</p>
          </header>
          <RankList rows={data.byCounterparty.receivable} color={COLORS.in} emptyText="Nenhuma conta a receber no período." />
        </section>

        <section className="ecoCard finDash__card">
          <header className="finDash__head">
            <h2>Próximos vencimentos</h2>
            <p>Em aberto, a partir de hoje.</p>
          </header>
          <ItemList items={data.upcoming} today={data.today} emptyText="Nada a vencer." />
        </section>

        <section className="ecoCard finDash__card">
          <header className="finDash__head">
            <h2>Maiores atrasados</h2>
            <p>Vencidos e ainda em aberto.</p>
          </header>
          <ItemList items={data.biggestOverdue} today={data.today} emptyText="Nada atrasado." />
        </section>
      </div>
    </div>
  );
}

const DashLegend = () => (
  <p className="finDash__legend">
    <span>
      <Swatch color={COLORS.out} /> A pagar
    </span>
    <span>
      <Swatch color={COLORS.in} /> A receber
    </span>
  </p>
);
